// Aba "Conciliação Pgto" do /caixa (migration 0341) — o espelho da Conciliação Bancária para o
// dinheiro que SAI: os pagamentos que a loja fez pela conta Mercado Pago (boleto pago com saldo,
// Pix enviado), vindos do mesmo feed do MP que traz os recebimentos (ehSaida, DA-352).
//
// Mostra os do dia escolhido e, junto, os de dias anteriores que ainda nao tem titulo (pedido dele,
// 23/10/2026). A conciliacao e' manual: Vincular → escolher o titulo do Contas a Pagar → o modal de
// baixa de sempre abre com a conta Mercado Pago e este pagamento. Um pagamento pode quitar varios
// titulos (Vincular de novo). Vale daqui pra frente: nada antes de 23/10 entra na tabela.
//
// Conciliado (com titulo) some da lista; "Mostrar conciliados" traz de volta — igual a Conciliacao
// Bancaria (pedido dele, 23/10/2026). Pra vincular um 2o titulo ao mesmo pagamento, marca a caixa.
//
// Tres fontes: a consulta do MP (boleto, Pix entre contas MP), o e-mail "Seu Pix foi enviado" (Pix
// pra outro banco / QR, com nome) e o relatorio "Dinheiro em conta" (toda saida, sem nome, ~1h de
// atraso — mp-relatorio.service). Titulo baixado como "nao aparece no MP" antes de o relatorio
// trazer o Pix se liga a ele pelo Vincular (secao "Ja baixados"), sem nova baixa.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Loader2, Link, Check } from 'lucide-react';
import { api } from '../../lib/api';
import { useSocket } from '../../lib/socket';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { Modal } from '../../components/Modal';
import { formatMoeda, formatDataHoraSP, formatHoraSP, formatDataSP, contar } from '@fiodecobre/shared';
import { ModalBaixarContaPagar, rotuloMpPagamento, type MpPagamentoEnviado } from '../contas-pagar/ModalBaixarContaPagar';

// De onde o pagamento chegou (pedido dele, 24/10/2026).
const ORIGEM: Record<'consulta' | 'email' | 'relatorio', string> = {
  consulta: 'pela consulta do MP',
  email: 'pelo e-mail do MP',
  relatorio: 'pelo relatório do MP',
};

const hojeISO = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
const diaSP = (iso: string) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date(iso));

export function ConciliacaoPagamentosTab({ podeBaixar }: { podeBaixar: boolean }) {
  const toast = useToast();
  const { socket } = useSocket();
  const [dia, setDia] = useState(hojeISO());
  const [mostrarConciliados, setMostrarConciliados] = useState(false);
  const [lista, setLista] = useState<MpPagamentoEnviado[]>([]);
  const [loading, setLoading] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [vincular, setVincular] = useState<MpPagamentoEnviado | null>(null);
  const [baixar, setBaixar] = useState<{ conta: any; pagamento: MpPagamentoEnviado } | null>(null);

  const carregar = useCallback(async () => {
    try {
      setLista(await api.get<MpPagamentoEnviado[]>(`/api/contas-pagar/mp-pagamentos?dia=${dia}`));
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setLoading(false);
    }
  }, [dia]);

  useEffect(() => { setLoading(true); void carregar(); }, [carregar]);

  // O vigia do MP avisa quando um pagamento novo da loja chega — a lista se rele sozinha.
  useEffect(() => {
    if (!socket) return;
    const onNovo = () => { void carregar(); };
    socket.on('mp:pagamento_enviado', onNovo);
    return () => { socket.off('mp:pagamento_enviado', onNovo); };
  }, [socket, carregar]);

  const pendentes = lista.filter((p) => p.titulos.length === 0).length;
  const visiveis = mostrarConciliados ? lista : lista.filter((p) => p.titulos.length === 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          Dia
          <input type="date" value={dia} onChange={(e) => setDia(e.target.value)} style={{ width: 160 }} />
        </label>
        <button type="button" className="btn-secundario" onClick={() => setDia(hojeISO())}>Hoje</button>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={mostrarConciliados} onChange={(e) => setMostrarConciliados(e.target.checked)} />
          Mostrar conciliados
        </label>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {pendentes > 0
            ? <strong style={{ color: 'var(--danger,#dc2626)' }}>{pendentes} a conciliar</strong>
            : 'tudo conciliado'}
        </span>
        <button type="button" className="btn-secundario" disabled={atualizando}
          onClick={async () => { setAtualizando(true); await carregar(); setAtualizando(false); }}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {atualizando ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
          Atualizar
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
      ) : visiveis.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          {lista.length > 0
            ? `Tudo conciliado neste dia (${contar(lista.length, 'pagamento')}). Marque "Mostrar conciliados" para ver.`
            : 'Nenhum pagamento pela conta Mercado Pago neste dia, e nada pendente de dias anteriores.'}
        </div>
      ) : (
        <table className="tabela" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Hora</th>
              <th>Tipo</th>
              <th style={{ textAlign: 'right' }}>Valor</th>
              <th>Banco / chave</th>
              <th>Título</th>
              <th style={{ textAlign: 'right', width: 150 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((p) => {
              const r = rotuloMpPagamento(p);
              const outroDia = p.data_pagamento && diaSP(p.data_pagamento) !== dia;
              const vinculado = Number(p.total_vinculado);
              const dif = Number(p.valor) - vinculado;
              const fornecedores = [...new Set(p.titulos.map((t) => t.fornecedor).filter((f): f is string => !!f))];
              return (
                <tr key={p.id}>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {p.data_pagamento ? (outroDia ? formatDataHoraSP(p.data_pagamento) : formatHoraSP(p.data_pagamento)) : '—'}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    <div>{r.tipo}</div>
                    {p.origem && (
                      <div style={{ color: 'var(--text-muted)' }}>
                        {ORIGEM[p.origem]}{p.nome_pelo_email && p.origem !== 'email' ? ' + nome pelo e-mail' : ''}
                      </div>
                    )}
                  </td>
                  <td className="cel-num" style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoeda(Number(p.valor))}</td>
                  <td style={{ fontSize: 12 }}>
                    <div>{r.detalhe || '—'}</div>
                    {/* O MP nao diz quem recebeu o boleto (so o banco emissor) — o fornecedor vem do titulo vinculado.
                        Sem titulo, vem da CONTA de destino ja conhecida (migration 0344, pedido dele 24/10/2026). */}
                    {fornecedores.length > 0
                      ? <div style={{ fontWeight: 700 }}>{fornecedores.join(' · ')}</div>
                      : p.fornecedor_conta && <div style={{ fontWeight: 700 }}>{p.fornecedor_conta}</div>}
                    {/* Pela regra dele a conta nunca muda de dono: titulo de OUTRO fornecedor ligado a ela se mostra. */}
                    {p.fornecedor_conta && fornecedores.length > 0 && !fornecedores.includes(p.fornecedor_conta) && (
                      <div style={{ color: '#b45309', fontWeight: 700 }}>esta conta é de {p.fornecedor_conta}</div>
                    )}
                    {p.conta_destino && <div style={{ color: 'var(--text-muted)' }}>conta {p.conta_destino}</div>}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {p.titulos.length === 0 ? (
                      <span style={{ color: 'var(--danger,#dc2626)', fontWeight: 700 }}>Sem título vinculado</span>
                    ) : (
                      <>
                        {p.titulos.map((t) => (
                          <div key={t.id}>
                            <Check size={12} style={{ display: 'inline', marginBottom: -2 }} /> {t.numero_documento || `CP #${t.id}`} · {t.descricao}
                            {' '}<span className="cel-num">{formatMoeda(Number(t.valor_pago))}</span>
                            {t.automatico && <span style={{ color: 'var(--text-muted)' }}> · vinculado pela conta</span>}
                          </div>
                        ))}
                        {Math.abs(dif) >= 0.005 && (
                          <div style={{ color: 'var(--text-muted)' }}>
                            {dif > 0 ? 'sobram' : 'passam'} <span className="cel-num">{formatMoeda(Math.abs(dif))}</span> do pagamento
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {podeBaixar && (
                      <button type="button" className={p.titulos.length === 0 ? 'btn-primario' : 'btn-secundario'}
                        style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        onClick={() => setVincular(p)}
                        title="Escolher o título do Contas a Pagar que este pagamento quitou">
                        <Link size={13} />{p.titulos.length === 0 ? 'Vincular' : 'Vincular outro'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {vincular && (
        <ModalEscolherTitulo
          pagamento={vincular}
          onFechar={() => setVincular(null)}
          onEscolher={(conta) => { setBaixar({ conta, pagamento: vincular }); setVincular(null); }}
          onLigado={() => { setVincular(null); void carregar(); }}
        />
      )}
      {baixar && (
        <ModalBaixarContaPagar
          conta={baixar.conta}
          mpPagamentoInicial={baixar.pagamento}
          onFechar={() => setBaixar(null)}
          onBaixado={() => { setBaixar(null); void carregar(); }}
        />
      )}
    </div>
  );
}

// Titulos PENDENTES do Contas a Pagar, os de valor mais perto do pagamento primeiro. Embaixo, os ja
// baixados como "nao aparece no MP": o relatorio do MP traz o Pix com atraso, e o titulo pode ter
// sido baixado antes dele aparecer — ai "Ligar" so troca a declaracao pelo vinculo, sem nova baixa.
function ModalEscolherTitulo({ pagamento, onFechar, onEscolher, onLigado }: {
  pagamento: MpPagamentoEnviado;
  onFechar: () => void;
  onEscolher: (conta: any) => void;
  onLigado: () => void;
}) {
  const toast = useToast();
  const dialog = useDialog();
  const [titulos, setTitulos] = useState<any[]>([]);
  const [semVinculo, setSemVinculo] = useState<any[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [ligando, setLigando] = useState<number | null>(null);
  const [busca, setBusca] = useState('');
  const valorPg = Number(pagamento.valor);
  const r = rotuloMpPagamento(pagamento);

  useEffect(() => {
    Promise.all([
      api.get<any[]>('/api/contas-pagar?status=pendente').then(setTitulos),
      api.get<any[]>('/api/contas-pagar/mp-sem-vinculo').then(setSemVinculo),
    ])
      .catch((e) => toast.erro(e.message))
      .finally(() => setCarregando(false));
  }, []);

  const filtrar = useCallback((ts: any[], campoValor: 'valor' | 'valor_pago') => {
    const q = busca.trim().toUpperCase();
    return ts
      .filter((t) => !q || [t.descricao, t.numero_documento, t.fornecedor_nome_fantasia, t.fornecedor_razao_social]
        .some((s) => String(s ?? '').toUpperCase().includes(q)))
      .sort((a, b) => Math.abs(Number(a[campoValor]) - valorPg) - Math.abs(Number(b[campoValor]) - valorPg));
  }, [busca, valorPg]);
  const lista = useMemo(() => filtrar(titulos, 'valor'), [filtrar, titulos]);
  const listaSemVinculo = useMemo(() => filtrar(semVinculo, 'valor_pago'), [filtrar, semVinculo]);

  const ligar = async (t: any) => {
    const ok = await dialog.confirmar(
      `Ligar este pagamento (${formatMoeda(valorPg)}) ao título ${t.numero_documento || `CP #${t.id}`} · ${t.descricao}, `
      + `já baixado como "não aparece no MP"?\n\nO título não é baixado de novo: só passa a ter este pagamento vinculado.`,
      { titulo: 'Ligar a título já baixado' },
    );
    if (!ok) return;
    setLigando(t.id);
    try {
      await api.post(`/api/contas-pagar/${t.id}/vincular-mp`, { mp_pagamento_enviado_id: pagamento.id });
      toast.sucesso('Pagamento ligado ao título');
      onLigado();
    } catch (e: any) {
      void dialog.alerta(e?.message || 'Falha ao ligar', { titulo: 'Não deu para ligar', tipo: 'erro' });
    } finally {
      setLigando(null);
    }
  };

  return (
    <Modal
      aberto={true}
      onFechar={onFechar}
      titulo="Vincular pagamento a um título"
      largura={760}
      rodape={<button type="button" className="btn-secundario" onClick={onFechar}>Cancelar</button>}
    >
      <div style={{ background: 'var(--card-bg)', borderRadius: 6, padding: 10, marginBottom: 12, fontSize: 13 }}>
        <strong>{r.tipo}</strong> · <span className="cel-num" style={{ fontWeight: 700 }}>{formatMoeda(valorPg)}</span>
        {pagamento.data_pagamento && <> · {formatDataHoraSP(pagamento.data_pagamento)}</>}
        {r.detalhe && <> · {r.detalhe}</>}
      </div>
      <input type="text" placeholder="Buscar por descrição, documento ou fornecedor..." value={busca}
        onChange={(e) => setBusca(e.target.value)} style={{ width: '100%', marginBottom: 10 }} autoFocus />
      {carregando ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
      ) : lista.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhum título pendente{busca ? ' nesta busca' : ''}.</div>
      ) : (
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          <table className="tabela" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Vencimento</th>
                <th>Documento</th>
                <th>Descrição / fornecedor</th>
                <th style={{ textAlign: 'right' }}>Valor</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {lista.map((t) => {
                const igual = Math.abs(Number(t.valor) - valorPg) < 0.005;
                return (
                  <tr key={t.id} style={igual ? { backgroundColor: '#fde68a', color: '#000' } : undefined}>
                    <td style={{ fontSize: 12 }}>{formatDataSP(t.data_vencimento)}</td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{t.numero_documento || '—'}</td>
                    <td style={{ fontSize: 12 }}>
                      <div>{t.descricao}</div>
                      {(t.fornecedor_nome_fantasia || t.fornecedor_razao_social) && (
                        <div style={{ color: igual ? '#000' : 'var(--text-muted)' }}>{t.fornecedor_nome_fantasia || t.fornecedor_razao_social}</div>
                      )}
                    </td>
                    <td className="cel-num" style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoeda(Number(t.valor))}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button type="button" className="btn-primario" style={{ fontSize: 12 }} onClick={() => onEscolher(t)}>Escolher</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!carregando && listaSemVinculo.length > 0 && (
        <>
          <div style={{ fontWeight: 700, fontSize: 13, margin: '16px 0 6px' }}>
            Já baixados como “não aparece no MP”
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            <table className="tabela" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Pago em</th>
                  <th>Documento</th>
                  <th>Descrição / fornecedor</th>
                  <th style={{ textAlign: 'right' }}>Valor pago</th>
                  <th style={{ width: 90 }} />
                </tr>
              </thead>
              <tbody>
                {listaSemVinculo.map((t) => {
                  const igual = Math.abs(Number(t.valor_pago) - valorPg) < 0.005;
                  return (
                    <tr key={t.id} style={igual ? { backgroundColor: '#fde68a', color: '#000' } : undefined}>
                      <td style={{ fontSize: 12 }}>{formatDataSP(t.data_pagamento)}</td>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{t.numero_documento || '—'}</td>
                      <td style={{ fontSize: 12 }}>
                        <div>{t.descricao}</div>
                        {(t.fornecedor_nome_fantasia || t.fornecedor_razao_social) && (
                          <div style={{ color: igual ? '#000' : 'var(--text-muted)' }}>{t.fornecedor_nome_fantasia || t.fornecedor_razao_social}</div>
                        )}
                      </td>
                      <td className="cel-num" style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoeda(Number(t.valor_pago))}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button type="button" className="btn-primario" style={{ fontSize: 12 }}
                          disabled={ligando !== null} onClick={() => void ligar(t)}>
                          {ligando === t.id ? 'Ligando...' : 'Ligar'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}
